import { useState, useMemo } from 'react';
import { FiCpu, FiInfo, FiCopy, FiCheck } from 'react-icons/fi';

interface CodeHighlighterProps {
  code: string;
  highlightedLines: Record<number, string>;
  warnings?: string[];
  suggestions?: string[];
  showLineNumbers?: boolean;
  className?: string;
}

/**
 * CodeHighlighter - Displays generated code with AI-generated line highlights.
 *
 * Features:
 * - Line numbers
 * - AI-generated explanations for specific lines (shown on hover)
 * - Visual markers for AI-generated sections
 * - Copy to clipboard
 * - Warnings and suggestions display
 */
export default function CodeHighlighter({
  code,
  highlightedLines,
  warnings = [],
  suggestions = [],
  showLineNumbers = true,
  className = '',
}: CodeHighlighterProps) {
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const lines = useMemo(() => code.split('\n'), [code]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <FiCpu className="w-4 h-4 text-profit" />
          <span className="text-sm text-gray-300">AI Generated Strategy</span>
          {Object.keys(highlightedLines).length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-profit/20 text-profit rounded">
              {Object.keys(highlightedLines).length} annotated lines
            </span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {copied ? (
            <>
              <FiCheck className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Copied</span>
            </>
          ) : (
            <>
              <FiCopy className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code area */}
      <div className="relative overflow-x-auto text-left">
        <pre className="p-4 text-sm font-mono text-left">
          {lines.map((line, idx) => {
            const lineNumber = idx + 1;
            const hasHighlight = lineNumber in highlightedLines;
            const isHovered = hoveredLine === lineNumber;

            return (
              <div
                key={idx}
                className={`flex transition-colors ${
                  hasHighlight
                    ? 'bg-profit/10 hover:bg-profit/20'
                    : 'hover:bg-gray-800/50'
                }`}
                onMouseEnter={() => setHoveredLine(lineNumber)}
                onMouseLeave={() => setHoveredLine(null)}
              >
                {/* Line number */}
                {showLineNumbers && (
                  <span className="w-12 flex-shrink-0 text-right pr-4 text-gray-600 select-none">
                    {lineNumber}
                  </span>
                )}

                {/* AI marker */}
                <span className="w-4 flex-shrink-0">
                  {hasHighlight && (
                    <FiCpu className="w-3 h-3 text-profit" title="AI-generated" />
                  )}
                </span>

                {/* Code line */}
                <code className="flex-1 text-gray-300 whitespace-pre">
                  {highlightSyntax(line)}
                </code>

                {/* Tooltip for highlighted lines */}
                {hasHighlight && isHovered && (
                  <div className="absolute right-4 z-10 max-w-xs px-3 py-2 bg-gray-800 border border-gray-700 rounded-md shadow-lg">
                    <p className="text-sm text-gray-300">{highlightedLines[lineNumber]}</p>
                  </div>
                )}
              </div>
            );
          })}
        </pre>
      </div>

      {/* Warnings & Suggestions - two column layout */}
      {(warnings.length > 0 || suggestions.length > 0) && (
        <div className="grid grid-cols-2 gap-4 px-3 py-2.5 bg-gray-800/50 border-t border-gray-700/50 text-left text-xs">
          {/* Warnings column */}
          <div className={warnings.length === 0 ? 'opacity-0' : ''}>
            <p className="text-amber-400 font-medium mb-1.5 flex items-center gap-1">
              <FiInfo className="w-3 h-3" />
              Warnings
            </p>
            <ul className="space-y-1">
              {warnings.map((warning, idx) => (
                <li key={idx} className="text-amber-300/80 leading-snug">{warning}</li>
              ))}
            </ul>
          </div>

          {/* Suggestions column */}
          <div className={suggestions.length === 0 ? 'opacity-0' : ''}>
            <p className="text-blue-400 font-medium mb-1.5 flex items-center gap-1">
              <FiInfo className="w-3 h-3" />
              Suggestions
            </p>
            <ul className="space-y-1">
              {suggestions.map((suggestion, idx) => (
                <li key={idx} className="text-blue-300/80 leading-snug">{suggestion}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simple syntax highlighting for Python code.
 */
function highlightSyntax(line: string): JSX.Element[] {
  let remaining = line;

  const comments = /(#.*$)/g;

  const tokens: Array<{ type: string; text: string; index: number }> = [];

  let match;

  // Comments (take precedence)
  const commentMatch = remaining.match(comments);
  if (commentMatch) {
    const idx = remaining.indexOf(commentMatch[0]);
    if (idx > 0) {
      tokens.push({ type: 'text', text: remaining.substring(0, idx), index: 0 });
    }
    tokens.push({ type: 'comment', text: commentMatch[0], index: idx });
    remaining = '';
  }

  if (remaining) {
    // String literals
    let lastIndex = 0;
    const stringRegex = /("[^"]*"|'[^']*')/g;
    while ((match = stringRegex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({ type: 'text', text: remaining.substring(lastIndex, match.index), index: lastIndex });
      }
      tokens.push({ type: 'string', text: match[0], index: match.index });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
      tokens.push({ type: 'text', text: remaining.substring(lastIndex), index: lastIndex });
    }
  }

  // Render tokens
  if (tokens.length === 0) {
    return [<span key={0}>{line}</span>];
  }

  return tokens.map((token, idx) => {
    if (token.type === 'comment') {
      return <span key={idx} className="text-gray-500">{token.text}</span>;
    }
    if (token.type === 'string') {
      return <span key={idx} className="text-green-400">{token.text}</span>;
    }
    // Highlight keywords in text tokens
    const text = token.text;
    const parts: JSX.Element[] = [];
    let lastIdx = 0;
    const kwRegex = /\b(def|class|if|elif|else|for|while|return|import|from|as|try|except|with|in|and|or|not|True|False|None|self)\b/g;
    let kwMatch;
    while ((kwMatch = kwRegex.exec(text)) !== null) {
      if (kwMatch.index > lastIdx) {
        parts.push(<span key={`${idx}-${parts.length}`}>{text.substring(lastIdx, kwMatch.index)}</span>);
      }
      parts.push(<span key={`${idx}-${parts.length}`} className="text-purple-400">{kwMatch[0]}</span>);
      lastIdx = kwMatch.index + kwMatch[0].length;
    }
    if (lastIdx < text.length) {
      parts.push(<span key={`${idx}-${parts.length}`}>{text.substring(lastIdx)}</span>);
    }
    return <span key={idx}>{parts.length > 0 ? parts : text}</span>;
  });
}
